/* LT-AO-01 — Tender Assumptions, Clarifications & Omissions tab.
 *
 * Layout:
 *   1) Sub-header strip — current job type + edit popover + 3 counter cards
 *      (Assumptions / Clarifications / Omissions ticked counts).
 *   2) Three side-by-side section cards (Assumption / Clarification / Omission)
 *      each with: a header counter, "Add custom statement" inline form, then
 *      list of items (custom items first, then library items by display_order).
 *      Each row has: tick checkbox, statement_text (inline editable), edit
 *      pencil/reset/promote/three-dot menu.
 *   3) Audit drawer — right-side slide-in (560px) showing per-line history.
 *
 * State model:
 *   `data` mirrors backend shape `{ items, summary, tender, seeded }`. Local
 *   optimistic ticks/edits write into `data.items`; debounced PUT flushes via
 *   `flushSave()`. Server response replaces `data` so summaries always match
 *   what the backend computed.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, BookOpen, ChevronDown, History, MoreHorizontal,
  Pencil, Plus, RefreshCw, Trash2, Upload, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useJobTypeLibrary } from "../hooks/useJobTypeLibrary";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";

const cls = (...a) => a.filter(Boolean).join(" ");

const TYPE_META = [
  { key: "assumption",    label: "Assumptions",    accentBg: "#E8EFE9", accentTxt: "#2F4F3E" },
  { key: "clarification", label: "Clarifications", accentBg: "#E0EAF1", accentTxt: "#1F4F73" },
  { key: "omission",      label: "Omissions",      accentBg: "#FEF3E2", accentTxt: "#7A4C0A" },
];

function formatTimestamp(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  } catch {
    return "";
  }
}

/* ============================================================
 * JobTypePopover — sub-header pencil that re-runs the template
 * ============================================================ */
function JobTypePopover({ tender, onPick, activeTypes, labelFor }) {
  const [open, setOpen] = useState(false);
  const current = tender?.job_type || null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="ao-job-type-edit"
          className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md border border-line bg-surface text-ink-2 hover:bg-surface-2 transition-colors"
          style={{ fontSize: 11.5 }}
          title="Change job type — auto-tick template will re-run"
        >
          <span className="font-mono uppercase text-ink-3"
            style={{ fontSize: 9, letterSpacing: "0.06em", fontWeight: 700 }}>
            Job type
          </span>
          <span style={{ fontWeight: 500 }}>
            {current ? labelFor(current) : "Unspecified"}
          </span>
          <Pencil size={11} className="text-ink-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom" align="start" sideOffset={6}
        avoidCollisions collisionPadding={16}
        className="w-[260px] p-2 z-50"
      >
        <div className="text-ink-3 px-2 pb-1.5" style={{ fontSize: 11 }}>
          Pick a job type — A&O auto-tick rules will re-run.
        </div>
        <div className="flex flex-col">
          {activeTypes.map((jt) => (
            <button key={jt.key}
              onClick={() => { onPick(jt.key); setOpen(false); }}
              data-testid={`ao-job-type-option-${jt.key}`}
              className={cls(
                "text-left px-2 py-1.5 rounded hover:bg-surface-2 transition-colors",
                current === jt.key && "bg-accent-soft text-accent",
              )}
              style={{ fontSize: 12 }}
            >
              {jt.label}
            </button>
          ))}
          {activeTypes.length === 0 && (
            <div className="px-2 py-2 text-ink-3 italic" style={{ fontSize: 11.5 }}>
              No active job types — add one in Settings → Job Types.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ============================================================
 * SubHeaderStrip — job type + 3 counter cards
 * ============================================================ */
function SubHeaderStrip({ tender, summary, onPickJobType, activeTypes, labelFor }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-3 mb-3">
        <JobTypePopover tender={tender} onPick={onPickJobType} activeTypes={activeTypes} labelFor={labelFor} />
        <span className="text-ink-3" style={{ fontSize: 11.5 }}>
          changing this re-runs the auto-tick template — your manual changes are preserved
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {TYPE_META.map(({ key, label, accentBg, accentTxt }) => {
          const c = summary?.[key] || { total: 0, ticked: 0 };
          return (
            <div
              key={key}
              className="bg-surface border border-line rounded-lg p-4"
              style={{
                boxShadow:
                  "0 1px 0 rgba(0,0,0,0.02), 0 6px 12px -6px rgba(58, 50, 32, 0.08), 0 2px 4px -2px rgba(58, 50, 32, 0.04)",
              }}
              data-testid={`ao-counter-${key}`}
            >
              <div
                className="col-head inline-flex items-center px-2 rounded mb-2"
                style={{ background: accentBg, color: accentTxt, padding: "2px 6px", fontSize: 9, letterSpacing: "0.06em", fontWeight: 700 }}
              >
                {label}
              </div>
              <div className="font-mono tnum text-ink" style={{ fontSize: 22, fontWeight: 600 }}>
                <span data-testid={`ao-counter-ticked-${key}`}>{c.ticked}</span>
                <span className="text-ink-3" style={{ fontSize: 14 }}>/{c.total}</span>
                <span className="text-ink-3 ml-2" style={{ fontSize: 11.5 }}>included</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
 * PromoteDialog — promote a custom item to library
 * ============================================================ */
function PromoteDialog({ open, item, onClose, onSubmit, activeTypes, labelFor }) {
  const [text, setText] = useState("");
  const [tags, setTags] = useState([]);
  useEffect(() => {
    if (open) {
      setText(item?.statement_text || "");
      setTags([]);
    }
  }, [open, item]);
  if (!open) return null;
  const submit = () => {
    if (!text.trim()) {
      toast.error("Statement is required");
      return;
    }
    onSubmit({ statement_text: text.trim(), applies_to_job_types: tags });
  };
  return (
    <>
      <div className="fixed inset-0 bg-ink/40 z-50" onClick={onClose} />
      <div
        className="fixed inset-0 z-50 flex items-start justify-center pt-20 pointer-events-none"
        data-testid="ao-promote-dialog"
      >
        <div className="bg-surface border border-line rounded-lg shadow-card w-[600px] max-w-[90vw] pointer-events-auto overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-line">
            <h2 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
              Promote to A&O Library
            </h2>
            <button onClick={onClose} className="text-ink-3 hover:text-ink"><X size={16} /></button>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <div className="text-ink-3 mb-2" style={{ fontSize: 11.5 }}>
                Type: <span className="font-mono uppercase text-ink-2">{item?.type}</span>
              </div>
              <label className="col-head text-ink-3 block mb-1">Statement text</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                spellCheck="true"
                data-testid="ao-promote-text"
                className="w-full h-24 p-2 rounded border border-line bg-surface text-ink resize-none focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                style={{ fontSize: 12.5 }}
                autoFocus
              />
            </div>
            <div>
              <label className="col-head text-ink-3 block mb-2">Applies to job types</label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setTags(["all"])}
                  data-testid="ao-promote-tag-all"
                  className={cls(
                    "px-2.5 h-7 rounded-md border transition-colors",
                    tags.includes("all")
                      ? "bg-accent text-white border-accent"
                      : "border-line bg-surface text-ink-2 hover:bg-surface-2",
                  )}
                  style={{ fontSize: 11.5 }}
                >All job types</button>
                {activeTypes.map((jt) => (
                  <button key={jt.key}
                    onClick={() => {
                      setTags((t) => {
                        const without = t.filter((x) => x !== "all");
                        return without.includes(jt.key) ? without.filter((x) => x !== jt.key) : [...without, jt.key];
                      });
                    }}
                    data-testid={`ao-promote-tag-${jt.key}`}
                    disabled={tags.includes("all")}
                    className={cls(
                      "px-2.5 h-7 rounded-md border transition-colors",
                      tags.includes(jt.key) && !tags.includes("all")
                        ? "bg-accent text-white border-accent"
                        : "border-line bg-surface text-ink-2 hover:bg-surface-2",
                      tags.includes("all") && "opacity-40 cursor-not-allowed",
                    )}
                    style={{ fontSize: 11.5 }}
                  >
                    {jt.label}
                  </button>
                ))}
              </div>
              <div className="text-ink-3 mt-2 italic" style={{ fontSize: 11 }}>
                Empty selection = item is added to the library but won't auto-tick on any job type.
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-line bg-surface-2/30">
            <button onClick={onClose}
              className="h-8 px-4 rounded-md text-ink-2 hover:bg-surface-2"
              style={{ fontSize: 12 }}
            >Cancel</button>
            <button onClick={submit}
              data-testid="ao-promote-submit"
              className="h-8 px-4 rounded-md bg-accent text-white hover:bg-accent-deep inline-flex items-center gap-1.5"
              style={{ fontSize: 12, fontWeight: 500 }}
            >
              <Upload size={12} /> Promote to library
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ============================================================
 * RowMenu — three-dot menu (audit / reset / delete / promote)
 * ============================================================ */
function RowMenu({ item, onOpenAudit, onReset, onDelete, onPromote }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`ao-row-menu-${item.id}`}
          className="text-ink-3 hover:text-ink-2 p-1"
        >
          <MoreHorizontal size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom" align="end" sideOffset={4}
        avoidCollisions collisionPadding={16}
        className="w-44 p-0 z-50 overflow-hidden"
      >
        <button
          onClick={() => { setOpen(false); onOpenAudit(item); }}
          className="w-full text-left px-3 py-2 text-ink-2 hover:bg-surface-2 inline-flex items-center gap-2"
          style={{ fontSize: 12 }}
        >
          <History size={12} className="text-ink-3" /> View audit history
        </button>
        {!item.is_custom && item.text_overridden && (
          <button
            onClick={() => { setOpen(false); onReset(item); }}
            data-testid={`ao-reset-${item.id}`}
            className="w-full text-left px-3 py-2 text-ink-2 hover:bg-surface-2 inline-flex items-center gap-2"
            style={{ fontSize: 12 }}
          >
            <RefreshCw size={12} className="text-ink-3" /> Reset to library
          </button>
        )}
        {item.is_custom && (
          <>
            <button
              onClick={() => { setOpen(false); onPromote(item); }}
              data-testid={`ao-promote-trigger-${item.id}`}
              className="w-full text-left px-3 py-2 text-ink-2 hover:bg-surface-2 inline-flex items-center gap-2"
              style={{ fontSize: 12 }}
            >
              <Upload size={12} className="text-info" /> Promote to library
            </button>
            <button
              onClick={() => { setOpen(false); onDelete(item); }}
              data-testid={`ao-delete-${item.id}`}
              className="w-full text-left px-3 py-2 text-note-red hover:bg-surface-2 inline-flex items-center gap-2"
              style={{ fontSize: 12 }}
            >
              <Trash2 size={12} /> Delete custom item
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

/* ============================================================
 * AOLine — single statement row
 * ============================================================ */
function AOLine({ item, onChange, onOpenAudit, onReset, onDelete, onPromote }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.statement_text || "");
  useEffect(() => { if (!editing) setDraft(item.statement_text || ""); }, [item.statement_text, editing]);

  const onSaveText = () => {
    setEditing(false);
    if ((draft || "").trim() !== (item.statement_text || "").trim()) {
      onChange({ statement_text: draft });
    }
  };

  return (
    <div
      data-testid={`ao-row-${item.id}`}
      className={cls(
        "group border-b border-line-soft last:border-b-0 px-3 py-2.5 hover:bg-surface-2/40",
        !item.is_ticked && "opacity-55"
      )}
    >
      <div className="flex items-start gap-2.5">
        <input
          type="checkbox"
          checked={!!item.is_ticked}
          onChange={(e) => onChange({ is_ticked: e.target.checked })}
          data-testid={`ao-tick-${item.id}`}
          className="mt-1 cursor-pointer"
          style={{ accentColor: "#2F4F3E" }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            {item.is_custom && (
              <span
                data-testid={`ao-badge-custom-${item.id}`}
                className="font-mono uppercase"
                style={{ background: "#E8EFE9", color: "#2F4F3E", padding: "1px 5px", fontSize: 9, fontWeight: 600, borderRadius: 3, letterSpacing: "0.05em" }}
              >Custom</span>
            )}
            {item.text_overridden && !item.is_custom && (
              <span
                data-testid={`ao-badge-overridden-${item.id}`}
                title="Edited from library default — Reset to library to revert"
                className="font-mono uppercase inline-flex items-center gap-0.5"
                style={{ background: "#FEF3E2", color: "#7A4C0A", padding: "1px 5px", fontSize: 9, fontWeight: 600, borderRadius: 3, letterSpacing: "0.05em" }}
              >
                <span style={{ fontSize: 8 }}>◆</span> Edited
              </span>
            )}
          </div>
          {editing ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={onSaveText}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onSaveText();
                if (e.key === "Escape") { setEditing(false); setDraft(item.statement_text || ""); }
              }}
              spellCheck="true"
              autoFocus
              data-testid={`ao-text-edit-${item.id}`}
              className="w-full p-2 rounded border border-accent bg-surface text-ink resize-none focus:outline-none focus:ring-2 focus:ring-accent/30"
              style={{ fontSize: 12.5, minHeight: 60 }}
            />
          ) : (
            <button
              onClick={() => setEditing(true)}
              data-testid={`ao-text-${item.id}`}
              className="text-left text-ink hover:text-accent transition-colors w-full block"
              style={{ fontSize: 12.5, lineHeight: "1.5" }}
              title="Click to edit"
            >
              {item.statement_text}
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-50 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <button
            onClick={() => setEditing(!editing)}
            data-testid={`ao-edit-pencil-${item.id}`}
            className="text-ink-3 hover:text-ink-2 p-1"
            title={editing ? "Cancel edit" : "Edit text"}
          >
            <Pencil size={12} />
          </button>
          <RowMenu
            item={item}
            onOpenAudit={onOpenAudit}
            onReset={onReset}
            onDelete={onDelete}
            onPromote={onPromote}
          />
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * AddCustomRow — inline form for new statement
 * ============================================================ */
function AddCustomRow({ type, onAdd }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid={`ao-add-custom-${type}`}
        className="w-full inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md border border-dashed border-line text-ink-3 hover:bg-surface-2 hover:text-accent hover:border-accent transition-colors"
        style={{ fontSize: 11.5 }}
      >
        <Plus size={12} /> Add custom statement
      </button>
    );
  }
  const submit = () => {
    if (!text.trim()) {
      toast.error("Statement is required");
      return;
    }
    onAdd(text.trim());
    setText(""); setOpen(false);
  };
  return (
    <div className="border border-accent rounded-md p-2.5 bg-surface-2/30">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        autoFocus
        spellCheck="true"
        placeholder="Custom statement…"
        data-testid={`ao-custom-text-${type}`}
        className="w-full p-2 rounded border border-line bg-surface text-ink resize-none focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
        style={{ fontSize: 12.5, minHeight: 60 }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
          if (e.key === "Escape") setOpen(false);
        }}
      />
      <div className="flex items-center justify-end gap-2 mt-2">
        <button onClick={() => setOpen(false)}
          className="h-7 px-3 rounded text-ink-3 hover:text-ink-2"
          style={{ fontSize: 11.5 }}
        >Cancel</button>
        <button onClick={submit}
          data-testid={`ao-custom-submit-${type}`}
          className="h-7 px-3 rounded bg-accent text-white hover:bg-accent-deep"
          style={{ fontSize: 11.5, fontWeight: 500 }}
        >Add</button>
      </div>
    </div>
  );
}

/* ============================================================
 * SectionCard — one of three (Assumption / Clarification / Omission)
 * ============================================================ */
function SectionCard({ meta, items, summary, onUpdate, onAddCustom, onOpenAudit, onReset, onDelete, onPromote }) {
  const c = summary?.[meta.key] || { total: 0, ticked: 0 };
  // Custom items first (pinned to top), then library items in display_order.
  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      if ((b.is_custom ? 1 : 0) - (a.is_custom ? 1 : 0) !== 0) {
        return (b.is_custom ? 1 : 0) - (a.is_custom ? 1 : 0);
      }
      return (a.display_order ?? 0) - (b.display_order ?? 0);
    });
  }, [items]);

  return (
    <div
      className="bg-surface border border-line rounded-lg overflow-hidden flex flex-col"
      style={{
        boxShadow:
          "0 1px 0 rgba(0,0,0,0.02), 0 6px 12px -6px rgba(58, 50, 32, 0.08), 0 2px 4px -2px rgba(58, 50, 32, 0.04)",
      }}
      data-testid={`ao-section-${meta.key}`}
    >
      <div
        className="px-4 py-3 border-b border-line flex items-center justify-between"
        style={{ background: meta.accentBg }}
      >
        <h2 className="font-display" style={{ color: meta.accentTxt, fontSize: 14, fontWeight: 600 }}>
          {meta.label}
        </h2>
        <span
          className="font-mono tnum"
          style={{ color: meta.accentTxt, fontSize: 12, fontWeight: 600 }}
          data-testid={`ao-section-counter-${meta.key}`}
        >
          {c.ticked}/{c.total} ticked
        </span>
      </div>
      <div className="flex-1 overflow-auto">
        {sorted.length === 0 ? (
          <div className="text-ink-3 text-center italic px-4 py-8" style={{ fontSize: 12 }}>
            Nothing in this section yet — add a custom statement below.
          </div>
        ) : (
          sorted.map((it) => (
            <AOLine
              key={it.id}
              item={it}
              onChange={(patch) => onUpdate(it.id, patch)}
              onOpenAudit={() => onOpenAudit(it)}
              onReset={() => onReset(it)}
              onDelete={() => onDelete(it)}
              onPromote={() => onPromote(it)}
            />
          ))
        )}
      </div>
      <div className="p-3 border-t border-line bg-surface-2/30">
        <AddCustomRow type={meta.key} onAdd={(text) => onAddCustom(meta.key, text)} />
      </div>
    </div>
  );
}

/* ============================================================
 * AuditDrawer — right slide-in
 * ============================================================ */
function AuditDrawer({ open, item, history, onClose }) {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 bg-ink/30 z-40" onClick={onClose} />
      <div
        className="fixed right-0 top-0 bottom-0 z-50 bg-surface border-l border-line shadow-card overflow-hidden flex flex-col"
        style={{ width: 560 }}
        data-testid="ao-audit-drawer"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div>
            <div className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
              Audit history
            </div>
            <div className="text-ink-3 mt-0.5 line-clamp-2" style={{ fontSize: 11.5 }}>
              {item?.statement_text}
            </div>
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink" data-testid="ao-audit-close">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {(!history || history.length === 0) ? (
            <div className="text-center py-12 text-ink-3" style={{ fontSize: 12.5 }}>
              No history yet.
            </div>
          ) : (
            <div className="divide-y divide-line-soft">
              {history.map((e, idx) => {
                const meta = e.metadata || {};
                let label = e.event_type;
                let detail = "";
                if (e.event_type === "ao_item_ticked") label = "Ticked";
                else if (e.event_type === "ao_item_unticked") label = "Unticked";
                else if (e.event_type === "ao_item_text_overridden") {
                  label = "Text overridden";
                  detail = `${meta.previous?.slice(0, 80)}\u2026 → ${meta.new?.slice(0, 80)}\u2026`;
                } else if (e.event_type === "ao_item_text_reverted") {
                  label = "Text reverted to library default";
                } else if (e.event_type === "ao_custom_item_added") {
                  label = "Added as custom statement";
                } else if (e.event_type === "ao_custom_item_promoted_to_library") {
                  label = "Promoted to A&O Library";
                } else if (e.event_type === "ao_item_reset") {
                  label = "Reset to library default";
                }
                return (
                  <div key={idx} className="px-5 py-3" data-testid={`ao-audit-row-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-ink" style={{ fontSize: 12.5, fontWeight: 500 }}>{label}</span>
                      <span className="text-ink-3 font-mono" style={{ fontSize: 11 }}>{formatTimestamp(e.created_at)}</span>
                    </div>
                    {detail && (
                      <div className="text-ink-3 mt-1" style={{ fontSize: 11.5 }}>
                        {detail}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ============================================================
 * Main TenderAO component
 * ============================================================ */
export default function TenderAO({ tender, onTenderUpdate }) {
  const [data, setData] = useState({ items: [], summary: null, tender });
  const [loading, setLoading] = useState(true);
  const [auditDrawer, setAuditDrawer] = useState({ item: null, history: [] });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [promote, setPromote] = useState({ open: false, item: null });
  const [status, setStatus] = useState("loaded");
  const { activeTypes, labelFor } = useJobTypeLibrary();

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tender.id]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tenders/${tender.id}/ao`);
      setData(r.data);
      if (r.data.tender) onTenderUpdate?.(r.data.tender);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load A&O");
    } finally {
      setLoading(false);
    }
  };

  // ----- save flow: optimistic + 600ms debounce per item -----
  const saveTimer = useRef(null);
  const pending = useRef({}); // { itemId: patch }
  const flush = async () => {
    const all = pending.current;
    pending.current = {};
    if (Object.keys(all).length === 0) return;
    setStatus("saving");
    try {
      let last = null;
      for (const [id, patch] of Object.entries(all)) {
        const r = await api.put(`/tenders/${tender.id}/ao/${id}`, patch);
        last = r.data;
      }
      if (last) {
        setData(last);
        if (last.tender) onTenderUpdate?.(last.tender);
      }
      setStatus("saved");
    } catch (e) {
      console.error(e);
      setStatus("error");
      toast.error("Save failed — your changes are safe locally.");
    }
  };
  const queueSave = (itemId, patch) => {
    pending.current[itemId] = { ...(pending.current[itemId] || {}), ...patch };
    setStatus("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, 600);
  };
  const onUpdate = (itemId, patch) => {
    setData((d) => ({
      ...d,
      items: d.items.map((it) => it.id === itemId ? { ...it, ...patch } : it),
      summary: recomputeSummaryLocal(d, itemId, patch),
    }));
    queueSave(itemId, patch);
  };

  // Optimistic summary update on local tick changes so the counters react
  // immediately (server response replaces with authoritative numbers).
  const recomputeSummaryLocal = (d, itemId, patch) => {
    if (!("is_ticked" in (patch || {}))) return d.summary;
    const before = d.items.find((i) => i.id === itemId);
    if (!before) return d.summary;
    const next = { ...d.summary };
    const bucket = next[before.type];
    if (!bucket) return next;
    if (before.is_ticked && !patch.is_ticked) {
      next[before.type] = { ...bucket, ticked: Math.max(0, bucket.ticked - 1) };
    } else if (!before.is_ticked && patch.is_ticked) {
      next[before.type] = { ...bucket, ticked: bucket.ticked + 1 };
    }
    return next;
  };

  const onAddCustom = async (type, statement_text) => {
    try {
      const r = await api.post(`/tenders/${tender.id}/ao`, { type, statement_text });
      setData(r.data);
      if (r.data.tender) onTenderUpdate?.(r.data.tender);
      toast.success("Custom statement added");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to add");
    }
  };

  const onDelete = async (item) => {
    if (!window.confirm("Delete this custom statement?")) return;
    try {
      const r = await api.delete(`/tenders/${tender.id}/ao/${item.id}`);
      setData(r.data);
      if (r.data.tender) onTenderUpdate?.(r.data.tender);
      toast.success("Deleted");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onReset = async (item) => {
    try {
      const r = await api.post(`/tenders/${tender.id}/ao/${item.id}/reset`);
      setData(r.data);
      if (r.data.tender) onTenderUpdate?.(r.data.tender);
      toast.success("Reverted to library default");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const onPickJobType = async (key) => {
    if (key === (data.tender?.job_type || null)) return;
    setStatus("saving");
    try {
      // 1. Save the new job_type onto the tender.
      const tu = await api.patch(`/tenders/${tender.id}`, { job_type: key });
      // 2. Trigger the recalculate-job-type-template route.
      const r = await api.post(`/tenders/${tender.id}/ao/recalculate-job-type-template`);
      setData({ ...r.data, tender: tu.data || r.data.tender });
      if (r.data.tender) onTenderUpdate?.(r.data.tender);
      setStatus("saved");
      const added = r.data.added_ticks || 0;
      const seeded = r.data.new_seeded || 0;
      if (added || seeded) {
        toast.success(`Job type updated · ${added} added, ${seeded} new`);
      } else {
        toast.success("Job type updated · no new auto-ticks");
      }
    } catch (e) {
      console.error(e);
      setStatus("error");
      toast.error("Failed to update job type");
    }
  };

  const onOpenAudit = async (item) => {
    setAuditDrawer({ item, history: [] });
    setDrawerOpen(true);
    try {
      const r = await api.get(`/tenders/${tender.id}/ao/audit/${item.id}`);
      setAuditDrawer({ item, history: r.data });
    } catch (e) {
      console.error(e);
    }
  };

  const onPromote = (item) => setPromote({ open: true, item });
  const onPromoteSubmit = async (payload) => {
    try {
      const r = await api.post(`/tenders/${tender.id}/ao/${promote.item.id}/promote`, payload);
      setData(r.data);
      if (r.data.tender) onTenderUpdate?.(r.data.tender);
      setPromote({ open: false, item: null });
      toast.success("Promoted to A&O Library");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Promote failed");
    }
  };

  const itemsByType = useMemo(() => {
    const m = { assumption: [], clarification: [], omission: [] };
    for (const it of data.items || []) {
      if (m[it.type]) m[it.type].push(it);
    }
    return m;
  }, [data.items]);

  if (loading) {
    return (
      <div className="mt-6 text-ink-3 py-12 text-center" style={{ fontSize: 12 }}>
        Loading A&O…
      </div>
    );
  }

  return (
    <div className="mt-6">
      <SubHeaderStrip
        tender={data.tender}
        summary={data.summary}
        onPickJobType={onPickJobType}
        activeTypes={activeTypes}
        labelFor={labelFor}
      />

      {/* Auto-save status */}
      <div className="flex items-center justify-end mb-2">
        <div className="text-ink-3 inline-flex items-center gap-2" style={{ fontSize: 11.5 }}>
          {status === "saving" && (
            <>
              <span className="inline-block w-1.5 h-1.5 bg-warning rounded-full animate-pulse" />
              Saving…
            </>
          )}
          {status === "saved" && (
            <>
              <span className="inline-block w-1.5 h-1.5 bg-accent rounded-full" />
              Saved
            </>
          )}
          {status === "error" && (
            <>
              <AlertTriangle size={12} className="text-note-red" />
              Save error
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-5" style={{ minHeight: 480 }}>
        {TYPE_META.map((meta) => (
          <SectionCard
            key={meta.key}
            meta={meta}
            items={itemsByType[meta.key] || []}
            summary={data.summary}
            onUpdate={onUpdate}
            onAddCustom={onAddCustom}
            onOpenAudit={onOpenAudit}
            onReset={onReset}
            onDelete={onDelete}
            onPromote={onPromote}
          />
        ))}
      </div>

      {/* Helper note */}
      <div className="text-ink-3 inline-flex items-center gap-2 mb-2" style={{ fontSize: 11.5 }}>
        <BookOpen size={12} />
        Master library at <a href="/ao-library" className="text-accent hover:underline">/ao-library</a>.
        Statements you add here can be promoted from each row's three-dot menu.
        Click <span className="font-mono">Down</span> on any statement to edit. <ChevronDown size={11} className="inline" />
      </div>

      <AuditDrawer
        open={drawerOpen}
        item={auditDrawer.item}
        history={auditDrawer.history}
        onClose={() => setDrawerOpen(false)}
      />

      <PromoteDialog
        open={promote.open}
        item={promote.item}
        onClose={() => setPromote({ open: false, item: null })}
        onSubmit={onPromoteSubmit}
        activeTypes={activeTypes}
        labelFor={labelFor}
      />
    </div>
  );
}
